import React, { useEffect, useRef, useMemo, useState } from 'react';
import { FileText, X, DownloadSimple, FileXls, FileImage, Presentation } from '@phosphor-icons/react';
import Vditor from 'vditor';
import 'vditor/dist/index.css';

interface FileViewerDrawerProps {
  name: string;
  content: string;
  onClose: () => void;
  /** If provided, enables download button */
  downloadUrl?: string;
}

const BINARY_EXTS = new Set(['pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);

/** Check if content is a base64 data URI */
function isDataUri(content: string): boolean {
  return content.startsWith('data:');
}

/** Extract mime type and create blob URL from data URI */
function dataUriToBlob(dataUri: string): { blobUrl: string; mimeType: string } | null {
  const match = dataUri.match(/^data:([^;]+);base64,(.+)$/s);
  if (!match) return null;
  const mimeType = match[1];
  const base64 = match[2];
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: mimeType });
  return { blobUrl: URL.createObjectURL(blob), mimeType };
}

function FileIcon({ ext }: { ext: string }) {
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileXls size={32} weight="light" style={{ color: '#2e7d32' }} />;
  if (['pptx', 'ppt'].includes(ext)) return <Presentation size={32} weight="light" style={{ color: '#e65100' }} />;
  if (IMAGE_EXTS.has(ext)) return <FileImage size={32} weight="light" style={{ color: '#1565c0' }} />;
  if (['pdf'].includes(ext)) return <FileText size={32} weight="light" style={{ color: '#ef4444' }} />;
  return <FileText size={32} weight="light" style={{ color: 'var(--brand)' }} />;
}

export default function FileViewerDrawer({ name, content, onClose, downloadUrl }: FileViewerDrawerProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const isMarkdown = ['md', 'markdown', 'txt', ''].includes(ext) && !isDataUri(content) && !!content;
  const isBinary = isDataUri(content) || BINARY_EXTS.has(ext);
  const isPdf = ext === 'pdf';
  const isImage = IMAGE_EXTS.has(ext);

  // Create blob URL from inline data URI content
  const inlineBlobInfo = useMemo(() => {
    if (!isDataUri(content)) return null;
    return dataUriToBlob(content);
  }, [content]);

  // Fetch binary from downloadUrl and create blob URL (for files stored on disk)
  const [fetchedBlobUrl, setFetchedBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (inlineBlobInfo || !downloadUrl || !isBinary) return;
    let revoked = false;
    fetch(downloadUrl)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (revoked) return;
        setFetchedBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { revoked = true; };
  }, [downloadUrl, isBinary, inlineBlobInfo]);

  // Combined blob info: prefer inline, then fetched
  const blobInfo = useMemo(() => {
    if (inlineBlobInfo) return inlineBlobInfo;
    if (fetchedBlobUrl) {
      // Guess mime from extension
      const mimeMap: Record<string, string> = {
        pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
        gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml',
      };
      return { blobUrl: fetchedBlobUrl, mimeType: mimeMap[ext] || 'application/octet-stream' };
    }
    return null;
  }, [inlineBlobInfo, fetchedBlobUrl, ext]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (inlineBlobInfo) URL.revokeObjectURL(inlineBlobInfo.blobUrl);
      if (fetchedBlobUrl) URL.revokeObjectURL(fetchedBlobUrl);
    };
  }, [inlineBlobInfo, fetchedBlobUrl]);

  // Render markdown preview
  useEffect(() => {
    if (!isMarkdown || !previewRef.current) return;
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    Vditor.preview(previewRef.current, content, {
      mode: isDark ? 'dark' : 'light',
      theme: { current: isDark ? 'dark' : 'light' },
      hljs: { style: isDark ? 'dracula' : 'github', lineNumber: true },
      markdown: { toc: true, mark: true, mermaid: true, mathBlockPreview: true } as any,
    });
  }, [content, isMarkdown]);

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else if (blobInfo) {
      const a = document.createElement('a');
      a.href = blobInfo.blobUrl;
      a.download = name;
      a.click();
    }
  };

  const renderContent = () => {
    // PDF preview
    if (isPdf && blobInfo) {
      return (
        <iframe
          src={blobInfo.blobUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title={name}
        />
      );
    }

    // Image preview
    if (isImage && blobInfo) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 20 }}>
          <img
            src={blobInfo.blobUrl}
            alt={name}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      );
    }

    // Other binary files — info card with download
    if (isBinary) {
      const sizeKB = blobInfo
        ? Math.round(atob(content.split(',')[1] || '').length / 1024)
        : 0;
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100%', gap: 16, padding: 32,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 18, background: 'var(--bg-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FileIcon ext={ext} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{name}</div>
            <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-muted)' }}>
              {ext.toUpperCase()} file{sizeKB > 0 ? ` · ${sizeKB >= 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB} KB`}` : ''}
            </div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginTop: 4 }}>
              This file type cannot be previewed in the browser
            </div>
          </div>
          <button
            onClick={handleDownload}
            style={{
              padding: '10px 24px', borderRadius: 8, fontSize: 'var(--fs-md)', fontWeight: 500,
              background: 'var(--brand)', color: 'white', border: 'none',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <DownloadSimple size={16} weight="light" />
            Download
          </button>
        </div>
      );
    }

    // Markdown preview
    if (isMarkdown) {
      return <div ref={previewRef} className="vditor-reset" style={{ padding: '20px 24px' }} />;
    }

    // Plain text / JSON / etc
    return (
      <pre style={{
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, padding: '20px 24px',
        fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit',
      }}>
        {content}
      </pre>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 40, animation: 'fadeIn 0.2s ease',
        }}
      />
      {/* Drawer */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, top: 36,
        background: 'var(--bg-surface)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -12px 48px var(--shadow)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        animation: 'slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        {/* Header bar */}
        <div style={{
          padding: '14px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} weight="light" style={{ color: '#0033FF' }} />
            <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>{name}</span>
            <span style={{
              fontSize: 'var(--fs-sm)', padding: '2px 8px', borderRadius: 6,
              background: '#e3f2fd', color: '#1565c0', fontWeight: 500,
            }}>
              read-only
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {(isBinary || downloadUrl) && (
              <button
                onClick={handleDownload}
                style={{
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--code-bg)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#6b7280',
                  transition: 'background 0.12s',
                }}
                title="Download"
              >
                <DownloadSimple size={14} weight="light" />
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--code-bg)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#6b7280',
                transition: 'background 0.12s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#e5e7eb')}
              onMouseOut={e => (e.currentTarget.style.background = 'var(--code-bg)')}
            >
              <X size={14} weight="light" />
            </button>
          </div>
        </div>
        {/* Content */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: isPdf ? 'hidden' : 'auto',
          fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--fs-lg)', lineHeight: 1.7, color: 'var(--text-primary)',
        }}>
          {renderContent()}
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
}
